import { GeoJson } from '@ais-ui/api-spec/shapes';
import {
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';
import { PiBracketsCurly } from 'react-icons/pi';
import { useRef, useState } from 'react';
import { DrawGeometryType } from '../isDrawGeometryType';
import {
  circleSchema,
  lineStringSchema,
  pointSchema,
  polygonSchema,
} from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { cn } from '@ais-ui/utils';
import { v4 as uuidv4 } from 'uuid';

/**
 * Validates geometry completeness for drawing (e.g., polygon needs 4+ points).
 * Returns an error message if invalid, or null if valid.
 */
const validateGeometryCompleteness = (
  geometryType: DrawGeometryType,
  geometry: Record<string, unknown>,
): string | null => {
  const coordinates = geometry.coordinates as
    | number[]
    | number[][]
    | number[][][];

  switch (geometryType) {
    case 'Point':
      return null;
    case 'LineString': {
      const points = coordinates as number[][];
      if (!points || points.length < 2) {
        return 'A line needs at least 2 points.';
      }
      return null;
    }
    case 'Polygon': {
      const rings = coordinates as number[][][];
      const outerRing = rings?.[0];
      if (!outerRing || outerRing.length < 4) {
        return 'A polygon needs at least 4 points (including the closing point).';
      }
      const first = outerRing[0];
      const last = outerRing[outerRing.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        return 'The first and last points of a polygon must be the same.';
      }
      return null;
    }
    case 'Circle':
      return null;
    default:
      return null;
  }
};

/**
 * Validates GeoJSON geometry structure based on geometry type.
 */
const isGeojsonValid = (
  geometryType: DrawGeometryType,
  newGeometry: Record<string, unknown>,
  radiusValue: number | undefined,
): boolean => {
  let validationResult;
  switch (geometryType) {
    case 'Circle':
      validationResult = circleSchema.safeParse({
        ...newGeometry,
        radius: radiusValue,
      });
      break;
    case 'Point':
      validationResult = pointSchema.safeParse(newGeometry);
      break;
    case 'LineString':
      validationResult = lineStringSchema.safeParse(newGeometry);
      break;
    case 'Polygon':
      validationResult = polygonSchema.safeParse(newGeometry);
      break;
    default:
      validationResult = {
        success: false,
        error: new Error('Unknown geometry type'),
      };
  }

  return validationResult.success;
};

export interface GeometryTextEditorPopoverProps {
  geometryType: DrawGeometryType;
  shape: GeoJson | null;
  onApply: (newShape: GeoJson) => void;
  buttonSize?: 'default' | 'sm' | 'lg' | 'icon';
  buttonVariant?: 'default' | 'outline' | 'ghost';
  buttonClassName?: string;
  popoverSide?: 'top' | 'bottom' | 'left' | 'right';
}

/**
 * GeometryTextEditorPopover — popover for editing geometry as GeoJSON text.
 *
 * Uses the established `useLocalization()` pattern from `@ais-ui/locale-management`
 * with inline English fallbacks so the component works even when translation keys
 * are missing (e.g. in apps with a minimal locale setup like data-forge).
 *
 * Required message keys (all optional — fallback to English):
 *   - `genericToastTitleError`
 *   - `shapesTextGeojsonError`
 *   - `textShapePopoverText`
 */
export const GeometryTextEditorPopover = ({
  geometryType,
  shape,
  onApply,
  buttonSize = 'icon',
  buttonVariant = 'outline',
  buttonClassName = 'size-10',
  popoverSide = 'left',
}: GeometryTextEditorPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { messages } = useLocalization();
  const radiusInputRef = useRef<HTMLInputElement>(null);
  const geojsonTextareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();

  const errorTitle = messages.genericToastTitleError || 'Error';
  const geojsonError =
    messages.shapesTextGeojsonError || 'Error parsing the GeoJSON';
  const tooltipText =
    messages.textShapePopoverText || 'See and edit shape in text-format';

  const onSubmitGeometry = () => {
    try {
      const radiusValue = radiusInputRef.current?.value
        ? Number(radiusInputRef.current.value)
        : undefined;
      const geojsonString = geojsonTextareaRef.current?.value || '';

      const newGeometry = JSON.parse(geojsonString);

      const isValid = isGeojsonValid(geometryType, newGeometry, radiusValue);

      if (!isValid) {
        toast({
          title: errorTitle,
          description:
            'Invalid GeoJSON structure. Please check coordinates and geometry type.',
          variant: 'destructive',
        });
        return;
      }

      const completenessError = validateGeometryCompleteness(
        geometryType,
        newGeometry,
      );
      if (completenessError) {
        toast({
          title: errorTitle,
          description: completenessError,
          variant: 'destructive',
        });
        return;
      }

      const newShape: GeoJson = {
        type: 'Feature',
        geometry: newGeometry,
        properties: {
          ...shape?.properties,
          id: shape?.properties?.id ?? uuidv4(),
          ...(geometryType === 'Circle' ? { radius: radiusValue } : {}),
        },
      };

      onApply(newShape);
      setIsOpen(false);
    } catch (error) {
      console.error('Error processing GeoJSON:', error);
      toast({
        title: errorTitle,
        description: geojsonError,
        variant: 'destructive',
      });
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal={true}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant={buttonVariant}
              size={buttonSize}
              className={cn(buttonClassName)}
            >
              <PiBracketsCurly className="size-5" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{tooltipText}</TooltipContent>
      </Tooltip>
      <PopoverContent side={popoverSide} className="w-96 h-96">
        <div className="flex flex-col w-full h-full space-y-4">
          {geometryType === 'Circle' && (
            <div className="flex flex-col space-y-2">
              <Label htmlFor="radius">Radius</Label>
              <Input
                id="radius"
                type="number"
                ref={radiusInputRef}
                defaultValue={
                  shape?.properties?.radius ? shape.properties.radius : 0
                }
              />
            </div>
          )}
          <div className="h-full flex flex-col space-y-2">
            <Label htmlFor="geojson">GeoJson</Label>
            <Textarea
              className="h-full"
              placeholder="Enter your geojson here"
              id="geojson"
              ref={geojsonTextareaRef}
              defaultValue={
                shape?.geometry ? JSON.stringify(shape.geometry, null, 2) : ''
              }
            />
          </div>
          <Button onClick={onSubmitGeometry}>Submit</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
